/**
 * The booking draft: where the visitor got to, saved in the browser so an
 * accidental tap outside the panel, a closed tab or a restart is not a
 * restart of the whole flow. On the next open the widget offers
 * "Resume booking" / "Start over"; resume re-checks with the server first
 * (the hold's state, or the chosen time's availability) and lands on the
 * saved step. Nothing here is trusted over the server.
 */
import type { PortalState } from './state';
import type { Step } from './types';

export interface BookingDraft {
  ts: number;
  step: Step;
  items: PortalState['items'];
  curr: PortalState['curr'];
  selectedDate: string | null;
  selectedTime: string | null;
  selectedLocationId: PortalState['selectedLocationId'];
  formData: Record<string, unknown>;
  bookForOther: boolean;
  otherFirstName: string; otherLastName: string; otherEmail: string; otherPhone: string;
  responsibleAgreed: boolean;
  groupSize: number;
  currentGuestIndex: number;
  guests: PortalState['guests'];
  agreedToPolicy: boolean;
  bkMarketingTexts: boolean;
  selectedProducts: PortalState['selectedProducts'];
  bookingCredit?: PortalState['bookingCredit'];
  /** Present once the booking exists and a deposit is being asked for. */
  deposit: PortalState['deposit'];
  depositPhoneLast4: string;
  depositTextState: PortalState['depositTextState'];
}

/** Steps worth coming back to. Success/pending screens and the resume screen itself are not drafts. */
export const DRAFT_STEPS: ReadonlySet<string> = new Set([
  'book-location', 'book-categories', 'book-enhancements', 'book-provider', 'book-addAnother',
  'book-datetime', 'book-contact', 'book-confirm', 'book-payment',
]);

/** A draft with no booking behind it is offered for a day; one on the deposit step for as long as the hold lasts. */
export const DRAFT_TTL_MS = 24 * 60 * 60 * 1000;

export function draftKey(apiBase: string): string {
  return `pp:draft:${(apiBase || '').replace(/[^a-z0-9]/gi, '').slice(-24)}`;
}

export function saveDraft(s: PortalState): void {
  if (!DRAFT_STEPS.has(s.step) || !s.apiBase) return;
  // An empty menu screen is not progress — nothing picked, nothing typed.
  const started = s.items.length > 0 || !!s.curr.service || !!s.deposit
    || Object.values(s.formData || {}).some(v => typeof v === 'string' ? v.trim() : !!v);
  if (!started) return;
  const d: BookingDraft = {
    ts: Date.now(), step: s.step,
    items: s.items, curr: s.curr,
    selectedDate: s.selectedDate, selectedTime: s.selectedTime, selectedLocationId: s.selectedLocationId,
    formData: s.formData, bookForOther: s.bookForOther,
    otherFirstName: s.otherFirstName, otherLastName: s.otherLastName, otherEmail: s.otherEmail, otherPhone: s.otherPhone,
    responsibleAgreed: s.responsibleAgreed, groupSize: s.groupSize, currentGuestIndex: s.currentGuestIndex, guests: s.guests,
    agreedToPolicy: s.agreedToPolicy, bkMarketingTexts: s.bkMarketingTexts, selectedProducts: s.selectedProducts,
    bookingCredit: s.bookingCredit,
    deposit: s.deposit, depositPhoneLast4: s.depositPhoneLast4, depositTextState: s.depositTextState,
  };
  try { localStorage.setItem(draftKey(s.apiBase), JSON.stringify(d)); } catch { /* private mode / quota — no draft */ }
}

export function loadDraft(apiBase: string, now: number = Date.now()): BookingDraft | null {
  try {
    const raw = localStorage.getItem(draftKey(apiBase));
    if (!raw) return null;
    const d = JSON.parse(raw) as BookingDraft;
    if (!d || !DRAFT_STEPS.has(d.step)) return null;
    if (d.deposit) {
      const until = Date.parse(d.deposit.expiresAt || '');
      if (!until || until <= now) { clearDraft(apiBase); return null; }
    } else if (!d.ts || now - d.ts > DRAFT_TTL_MS) {
      clearDraft(apiBase); return null;
    }
    return d;
  } catch { return null; }
}

export function clearDraft(apiBase: string): void {
  try { localStorage.removeItem(draftKey(apiBase)); } catch { /* ignore */ }
}

/** Restore the saved fields onto a state that already has its booking data loaded. */
export function applyDraft(s: PortalState, d: BookingDraft): void {
  s.items = d.items || [];
  s.curr = d.curr || { service: null, selectedAddons: [], provider: null, totalPrice: 0 };
  s.selectedDate = d.selectedDate; s.selectedTime = d.selectedTime; s.selectedLocationId = d.selectedLocationId;
  s.formData = { ...(s.formData || {}), ...(d.formData || {}) };
  s.bookForOther = !!d.bookForOther;
  s.otherFirstName = d.otherFirstName || ''; s.otherLastName = d.otherLastName || '';
  s.otherEmail = d.otherEmail || ''; s.otherPhone = d.otherPhone || '';
  s.responsibleAgreed = !!d.responsibleAgreed;
  s.groupSize = d.groupSize || 1; s.currentGuestIndex = d.currentGuestIndex || 0;
  // `guests` is SPARSE in memory (index 0 is the booker, never set), and JSON
  // turns the hole into null — a restored two-person draft then crashed at
  // submit with "null is not an object (evaluating 'g.firstName')" (Sep
  // 2026). Restore the shape: index 0 stays empty, every other slot is an
  // object.
  s.guests = [];
  (d.guests || []).forEach((g, i) => { if (i > 0) s.guests[i] = g || { firstName: '', lastName: '', phone: '', email: '' }; });
  s.agreedToPolicy = !!d.agreedToPolicy; s.bkMarketingTexts = !!d.bkMarketingTexts;
  s.selectedProducts = d.selectedProducts || [];
  s.bookingCredit = d.bookingCredit || null;
  s.deposit = d.deposit || null;
  s.depositPhoneLast4 = d.depositPhoneLast4 || '';
  s.depositTextState = d.depositTextState === 'sent' ? 'sent' : 'idle';
}

/** One line for the resume screen: what the draft is about. */
export function describeDraft(d: BookingDraft): string {
  const names = (d.items || []).map(i => i.service?.name).filter(Boolean) as string[];
  if (!names.length && d.curr?.service?.name) names.push(d.curr.service.name);
  const what = names.length ? names.join(' + ') : 'your booking';
  const when = d.selectedDate && d.selectedTime ? ` on ${d.selectedDate} at ${d.selectedTime}` : (d.selectedDate ? ` on ${d.selectedDate}` : '');
  return `${what}${when}`;
}
